import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { BrowserRouter } from 'react-router';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import theme from './lib/theme.tsx';
import { IntlProvider } from 'react-intl';
import { ErrorContextProvider } from './contexts/ErrorContext.tsx';
import { NotificationContextProvider } from './contexts/NotificationContext.tsx';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { UploadContextProvider } from './contexts/UploadContext.tsx';
import { DownloadContextProvider } from './contexts/DownloadContext.tsx';
import { BasketContextProvider } from './contexts/BasketContext.tsx';
import { Auth0Provider } from '@auth0/auth0-react';
import AuthContextProvider from './contexts/AuthContext.tsx';
import { CookieConsentProvider } from './contexts/CookieConsentContext.tsx';
import { getAuth0ApiAuthorizationParams } from './constants/auth0Api.ts';
import * as Sentry from '@sentry/react';
import 'dayjs/locale/en-gb';

Sentry.init({
  dsn: 'https://f1d0c8aab1ea58c9843423d03c0b5f73@o4507968173047808.ingest.de.sentry.io/4510419477397584',
  sendDefaultPii: true,
  environment: import.meta.env.VITE_E,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60,
      staleTime: 1000 * 60 * 60,
    },
  },
});

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const useAuth0LocalStorageCache =
  import.meta.env.VITE_AUTH0_LOCALSTORAGE_CACHE === 'true';

console.log(
  '%cFrontend: v' +
    __VERSION__ +
    ' # ' +
    __COMMIT_HASH__ +
    ' # ' +
    import.meta.env.VITE_E,
  'background: white; color: black; padding: 2px 4px;',
);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <IntlProvider messages={{}} locale="en">
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="en-gb"
            >
              <ErrorContextProvider>
                <NotificationContextProvider>
                  <CookieConsentProvider>
                    <UploadContextProvider>
                      <DownloadContextProvider>
                        <BasketContextProvider>
                          <Auth0Provider
                            domain={domain}
                            clientId={clientId}
                            {...(useAuth0LocalStorageCache
                              ? { cacheLocation: 'localstorage' as const }
                              : {})}
                            authorizationParams={{
                              redirect_uri: window.location.origin,
                              ...getAuth0ApiAuthorizationParams(),
                            }}
                          >
                            <AuthContextProvider>
                              <App />
                              <CssBaseline />
                            </AuthContextProvider>
                          </Auth0Provider>
                        </BasketContextProvider>
                      </DownloadContextProvider>
                    </UploadContextProvider>
                  </CookieConsentProvider>
                </NotificationContextProvider>
              </ErrorContextProvider>
            </LocalizationProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </IntlProvider>
    </BrowserRouter>
  </StrictMode>,
);
